//@flow
import AbstractAuthType, { AuthType } from './AuthType';
import BasicAuthType from './BasicAuthType';
import OAuthType from './OAuthType';
import AuthMechanism, { AuthRealm } from './AuthMechanism';

const debug = require('../debug/debug')('wc:auth:AuthTypeHandler');

/**
 * We will trap **401** errors in **HttpOperation** and based on header
 * construct the specific implementation of BaseRealm. Right now
 * supporting:
 *  - BasicAuthRealm. Header = WWW-Authenticate: Basic
 *  - PingAuthRealm.  Header = WWW-Authenticate: OAuth
 *
 * In Yuan goal is not touch the existing code that much and crate new code/classes
 * ,so that merging is easy. We will take advance of dynamic nature of javascript
 * and extend the methods of **HttpOperation** without touching it. We will ingest
 * new methods and change existing methods from outside of the class.
 */
export default class AuthTypeHandler {
  static AUTH_HEADER_NAME: string = 'Zm-Authenticate';

  constructor() {}

  static getAuthTypesFromFetchHeaders(headers: Headers): Array<AuthType> {
    const headerValue: ?string = headers != null ? headers.get(this.AUTH_HEADER_NAME) : null;
    if (debug.enabled) debug('[getAuthTypes(Headers)] Authenticate Header Value %s', headerValue);
    return this.extractAuthTypes(headerValue);
  }

  static getAuthTypes(jqueryResponse: XMLHttpRequest): Array<AuthType> {
    const headerValue: ?string = jqueryResponse.getResponseHeader(this.AUTH_HEADER_NAME);
    if (debug.enabled) debug('[getAuthTypes()] Authenticate Header Value %s', headerValue);
    return this.extractAuthTypes(headerValue);
  }

  static extractAuthTypes(headerValue: string): Array<AuthType> {
    const parsedAuthHeaders: Array<AuthHeader> = this.parseAuthenticateHeaderValue(headerValue);
    const authTypes: Array<AuthType> = [];
    if (debug.enabled && parsedAuthHeaders.length > 0)
      debug('[getAuthTypes() Extract values from Authenticate header value. %o', parsedAuthHeaders);
    for (let index = 0; index < parsedAuthHeaders.length; index++) {
      const element = parsedAuthHeaders[index];
      const authHeader: ?AuthHeader = element;
      if (authHeader && authHeader.realm) {
        const authRealm = new AuthRealm(authHeader.realm, authHeader.mechanism, authHeader.label);
        const authType: ?AuthType = this.createAuthType(authRealm);
        if (authType) {
          authTypes.push(authType);
        }
      }
    }

    return authTypes;
  }

  static createAuthType(authRealm: AuthRealm): ?AuthType {
    if (authRealm.authMechanism == AuthMechanism.Basic) {
      return new BasicAuthType(authRealm);
    } else if (authRealm.authMechanism == AuthMechanism.OAuth) {
      return new OAuthType(authRealm);
    } else {
      console.warn('Unknown auth realm: ', JSON.stringify(authRealm), 'Returning default Basic with Zoom realm');
      //return new BasicAuthType(new AuthRealm('Zoom', AuthMechanism.Basic));
      return null;
    }
  }

  /**
   *
   * Method takes the value of Zm-Authenticate header and extract the auth mechanism and realm from it.
   * It returns the Array of AuthHeader type.
   * Returned value will never be null.
   *
   * @static
   * @param {?string} headerValue
   * @returns {Array<AuthHeader>}
   * @memberof AuthTypeHandler
   */
  static parseAuthenticateHeaderValue(headerValue: ?string): Array<AuthHeader> {
    const collectedAuthMechanism: Array<AuthHeader> = [];
    if (!headerValue) {
      return collectedAuthMechanism;
    }
    /*
     * Header value can be like:
     * Zm-Authenticate: Basic realm="ZoomRealm", charset=utf-8, OAuth realm="PingRealm", Basic realm="ExternalZoomUser"
     */

    const splitOnComma: Array<string> = headerValue.split(',');

    let prevAuthHeader: AuthHeader = null;
    for (let index = 0; index < splitOnComma.length; index++) {
      const element = splitOnComma[index].trim();
      if (element.length > 0) {
        const authHeader: ?AuthHeader = this.extractAuthTypeAndRealm(element);
        if (!!authHeader === false) {
          if (!!prevAuthHeader === false || !this.extractAuthRealmProperties(element, prevAuthHeader)) {
            debug('[parseAuthenticateHeaderValue] Ignoring Authenticate header value part: %s', element);
          }
        } else {
          collectedAuthMechanism.push(authHeader);
          prevAuthHeader = authHeader;
        }
      }
    }
    return collectedAuthMechanism;
  }

  /**
   * Parses the string to extract the Auth Mechanism and realm name from the given string.
   * Given string must be in the format of [Auth Mechanism] realm="[Name]"
   * @param {?string} headerValue
   * @returns {?AuthHeader} Auth header with type and realm initialised, if found.
   */
  static extractAuthTypeAndRealm(headerValue: string): ?AuthHeader {
    let regEx: RegExp = /\s*([a-z]+)\s+realm="(.+)".*/i;
    let authHeader: ?AuthHeader = null;
    if (headerValue) {
      const values: ?string[] = headerValue.match(regEx);
      if (values && values.length === 3) {
        authHeader = {};
        const type: string = values[1];
        const realm: string = values[2];
        const authMechanism = AuthMechanism.toEnum(type, AuthMechanism.Basic);
        authHeader.mechanism = authMechanism;
        authHeader.type = type;
        authHeader.realm = realm;
      }
    }
    return authHeader;
  }

  /**
   * Parses the string to extract the additional properties of the realm.
   * @param {?string} headerValue
   * @param {AuthHeader} realm
   * @returns {Boolean} true - if found a usable property, false - otherwise.
   */
  static extractAuthRealmProperties(headerValue: string, realm: AuthHeader): boolean {
    let regEx: RegExp = /\s*([a-z]+)="(.+)".*/i;
    if (headerValue) {
      const values: ?string[] = headerValue.match(regEx);
      if (values && values.length == 3) {
        const propName: string = values[1];
        const propValue: string = values[2];
        if (propName == 'title') {
          realm.label = propValue;
          return true;
        }
      }
    }
    return false;
  }
}

export type AuthHeader = {
  mechanism: AuthMechanism, //"Basic" | "OAuth",
  type: ?string, //"Basic" | "OAuth",
  realm: ?string, // "ZoomRealm" | "ExtLdapRealm" | "PingRealm"
  label: ?string // "ZoomRealm" | "Single Sign-On" | "ZoomExternal"
};
